import { forwardRef } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@rtkwlf/pro-solid-svg-icons/faChevronRight';
import {
  type LinkProps as RouterLinkProps,
  Link as RouterLink,
} from 'react-router-dom';

import { type StrippedLinkProps, Link } from '..';
import { Stack } from '../../stack';
import { Typography } from '../../typography';

export interface LinkRouterProps
  extends Pick<
      RouterLinkProps,
      | 'reloadDocument'
      | 'replace'
      | 'state'
      | 'preventScrollReset'
      | 'relative'
      | 'to'
    >,
    Omit<StrippedLinkProps, 'href' | 'component'> {
  /**
   * If set to true, target='_blank' and rel='noopener noreferrer' are included
   * @default false
   */
  isExternal?: boolean;
  /**
   * Show an arrow next to the text
   * @default false
   */
  showArrow?: boolean;
}

export const LinkRouter = forwardRef<HTMLAnchorElement, LinkRouterProps>(
  (props: LinkRouterProps, ref) => {
    const {
      children,
      isExternal = false,
      variant = 'body1',
      noWrap,
      showArrow = false,
      ...rest
    } = props;
    const externalProps = isExternal
      ? { target: '_blank', rel: 'noopener noreferrer' }
      : undefined;

    return (
      <Link
        ref={ref}
        component={RouterLink}
        variant={variant}
        noWrap={!showArrow ? noWrap : undefined}
        {...rest}
        {...externalProps}
      >
        {showArrow ? (
          <Stack direction="row" spacing={1 / 2} sx={{ alignItems: 'center' }}>
            <Typography noWrap={noWrap} variant={variant}>
              {children}
            </Typography>
            <FontAwesomeIcon icon={faChevronRight} />
          </Stack>
        ) : (
          children
        )}
      </Link>
    );
  },
);
